"use client";

import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiSearch, FiChevronRight, FiChevronLeft, FiMapPin, FiPhone, FiMail, FiHome, FiUsers } from "react-icons/fi";
import dynamic from "next/dynamic";
import MapOverlayStats from "@/components/Common/MapOverlayStats";
import { useTranslations, useLocale } from "next-intl";
import { formatNumberByLocale } from "@/utils/numberFormatter";
import { usePartnersMap } from "@/hooks/usePartnersMap";
import { getStateCentroid } from "@/components/constants/stateCentroids";
import { getLocationCentroid, type LatLng } from "@/lib/geocode/locationCentroid";
import { reverseGeocode } from "@/lib/geocode/reverseGeocode";
import {
  groupByLevel,
  scopeToSelection,
  filterPartners,
  allShareField,
  partnersLabel,
  type LocationLevel,
  type LocationSelection,
} from "@/lib/partners";
import type { MapMarker, MapHighlight } from "@/components/Common/LeafletMap";
import type { Partner } from "@/types/partner";

// Super Partners are hidden for now (legend, stats row, purple map markers,
// list tab). Set to true to bring them back.
const SHOW_SUPER_PARTNER = false;

const LeafletMap = dynamic(() => import("@/components/Common/LeafletMap"), {
  ssr: false,
});

const ZOOM_BY_LEVEL: Record<LocationLevel, number> = {
  state: 7,
  district: 9,
  taluka: 11,
  village: 13,
};

const LOCATION_CONSENT_KEY = "find-partner-location-consent";

// Small deterministic offset (based on the group's own id) so that when
// several locations fall back to the same parent centroid (their own
// geocode failed), they don't render as one perfectly-overlapping pin.
function jitterFromParent(id: number | string, parent: LatLng): LatLng {
  const seed = typeof id === "number" ? id : Array.from(String(id)).reduce((sum, c) => sum + c.charCodeAt(0), 0);
  const angle = (seed % 360) * (Math.PI / 180);
  const radiusDegrees = 0.03; // roughly a few km, just enough to separate pins visually
  return {
    lat: parent.lat + radiusDegrees * Math.cos(angle),
    lng: parent.lng + radiusDegrees * Math.sin(angle),
  };
}

type SelectedNode = { id: number | string; name: string; lat: number; lng: number };
type SelectedNodes = Partial<Record<LocationLevel, SelectedNode>>;

const ContactInfo = ({ p }: { p: Partner }) => (
  <div className="mt-1 space-y-0.5 text-xs text-gray-600">
    {p.mobile && (
      <div className="flex items-center gap-1">
        <FiPhone className="h-3 w-3 shrink-0" />
        <span className="truncate">{p.mobile}</span>
      </div>
    )}
    {p.email && (
      <div className="flex items-center gap-1">
        <FiMail className="h-3 w-3 shrink-0" />
        <span className="truncate">{p.email}</span>
      </div>
    )}
    {p.address && (
      <div className="flex items-center gap-1">
        <FiHome className="h-3 w-3 shrink-0" />
        <span className="truncate">{p.address}</span>
      </div>
    )}
  </div>
);

const Page = () => {
  const t = useTranslations("findpartner");
  const locale = useLocale();

  const { partners, error } = usePartnersMap();

  const [selection, setSelection] = useState<LocationSelection>({});
  const [selectedNodes, setSelectedNodes] = useState<SelectedNodes>({});
  const [focus, setFocus] = useState<{ center: [number, number] | null; zoom: number }>({
    center: null,
    zoom: 6,
  });

  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Partner[] | null>(null);
  const [selectedSuperPartner, setSelectedSuperPartner] = useState<Partner | null>(null);
  const [highlight, setHighlight] = useState<MapHighlight | null>(null);
  const [activeTab, setActiveTab] = useState<"super_partner" | "partner">(
    SHOW_SUPER_PARTNER ? "super_partner" : "partner"
  );

  const [locationConsent, setLocationConsent] = useState<"unknown" | "prompt" | "granted" | "denied">("unknown");
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [nearbyInfo, setNearbyInfo] = useState<{ stateId: string | number; count: number } | null>(null);
  const autoLocationAppliedRef = useRef(false);

  // Read any earlier location-permission decision on first load.
  useEffect(() => {
    const stored = window.localStorage.getItem(LOCATION_CONSENT_KEY);
    setLocationConsent(stored === "granted" || stored === "denied" ? stored : "prompt");
  }, []);

  const applyUserLocation = async (lat: number, lng: number) => {
    // Deliberately does NOT touch `selection` — the map/list stay showing
    // everything as usual. We only compute which state is "nearby" so it can
    // be highlighted, plus a count, without hiding anything else.
    setFocus({ center: [lat, lng], zoom: ZOOM_BY_LEVEL.state });

    const geo = await reverseGeocode(lat, lng);
    if (!geo?.state) return;

    const stateMatch = partners.find(
      (p) => p.state_name && p.state_name.toLowerCase() === geo.state!.toLowerCase()
    );
    if (!stateMatch?.state_id) return;

    const nearbyCount = partners.filter((p) => String(p.state_id) === String(stateMatch.state_id)).length;
    setNearbyInfo({ stateId: stateMatch.state_id, count: nearbyCount });
  };

  const requestLocation = () => {
    // Dismiss our banner immediately on click — the browser's own native
    // permission dialog (and the GPS fix itself) can take a few seconds,
    // and that wait shouldn't keep our custom prompt on screen.
    setLocationConsent("granted");

    if (!navigator.geolocation) {
      window.localStorage.setItem(LOCATION_CONSENT_KEY, "denied");
      setLocationConsent("denied");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        window.localStorage.setItem(LOCATION_CONSENT_KEY, "granted");
        const { latitude, longitude } = pos.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        applyUserLocation(latitude, longitude);
      },
      () => {
        window.localStorage.setItem(LOCATION_CONSENT_KEY, "denied");
        setLocationConsent("denied");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const declineLocation = () => {
    window.localStorage.setItem(LOCATION_CONSENT_KEY, "denied");
    setLocationConsent("denied");
  };

  // For a returning user who already granted permission earlier: re-fetch
  // their location automatically once the partner list has loaded, without
  // showing the consent banner again.
  useEffect(() => {
    if (locationConsent !== "granted" || autoLocationAppliedRef.current || partners.length === 0) return;
    if (!navigator.geolocation) return;
    autoLocationAppliedRef.current = true;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        applyUserLocation(latitude, longitude);
      },
      () => {}
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationConsent, partners]);

  const currentLevel: LocationLevel = selection.taluka
    ? "village"
    : selection.district
    ? "taluka"
    : selection.state
    ? "district"
    : "state";

  const scopedPartners = useMemo(() => scopeToSelection(partners, selection), [partners, selection]);
  const groups = useMemo(() => groupByLevel(scopedPartners, currentLevel), [scopedPartners, currentLevel]);

  const [markerCoords, setMarkerCoords] = useState<Record<string, LatLng>>({});

  // Resolve marker coordinates for whichever level is currently on screen.
  // State pins come from the static lookup table; district/taluka/village pins
  // are geocoded on demand (only for the small set of names actually present
  // in the fetched data) and cached, so repeat visits are instant.
  useEffect(() => {
    if (currentLevel === "state") {
      const next: Record<string, LatLng> = {};
      groups.forEach((g) => {
        const c = getStateCentroid(g.name);
        if (c) next[`state-${g.id}`] = c;
      });
      setMarkerCoords((prev) => ({ ...prev, ...next }));
      return;
    }

    const level = currentLevel;
    let cancelled = false;

    // Nearest already-resolved ancestor, used as a fallback pin location
    // when this level's own geocode attempt fails (common for small
    // villages/talukas that Nominatim doesn't have).
    const parentCoord: LatLng | null =
      level === "district"
        ? selectedNodes.state ?? null
        : level === "taluka"
        ? selectedNodes.district ?? selectedNodes.state ?? null
        : selectedNodes.taluka ?? selectedNodes.district ?? selectedNodes.state ?? null;

    (async () => {
      for (const g of groups) {
        const key = `${level}-${g.id}`;
        if (markerCoords[key]) continue;
        const nameParts =
          level === "district"
            ? [g.name, selection.state?.name]
            : level === "taluka"
            ? [g.name, selection.district?.name, selection.state?.name]
            : [g.name, selection.taluka?.name, selection.district?.name, selection.state?.name];
        const coord = await getLocationCentroid(level, g.id, nameParts);
        if (cancelled) return;
        const finalCoord = coord ?? (parentCoord ? jitterFromParent(g.id, parentCoord) : null);
        if (finalCoord) setMarkerCoords((prev) => ({ ...prev, [key]: finalCoord }));
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, currentLevel, selectedNodes]);

  const markers: MapMarker[] = groups
    .map((g) => {
      const coord = markerCoords[`${currentLevel}-${g.id}`];
      if (!coord) return null;
      const superPartners = SHOW_SUPER_PARTNER
        ? g.partners.filter((p) => p.type === "super_partner")
        : [];
      const superPartnerCount = superPartners.length;
      const partnersUnderSuperPartnerCount = superPartners.reduce(
        (sum, p) => sum + (p.sub_partners?.length ?? 0),
        0
      );
      return {
        id: String(g.id),
        name: g.name,
        lat: coord.lat,
        lng: coord.lng,
        count: g.count,
        superPartnerCount,
        partnersUnderSuperPartnerCount,
      };
    })
    .filter((m): m is MapMarker => m !== null);

  const handleMarkerClick = (idStr: string) => {
    const group = groups.find((g) => String(g.id) === idStr);
    if (!group) return;
    const coord = markerCoords[`${currentLevel}-${group.id}`];
    if (!coord) return;

    setActiveTab(SHOW_SUPER_PARTNER && group.partners.some((p) => p.type === "super_partner") ? "super_partner" : "partner");

    const node: SelectedNode = { id: group.id, name: group.name, lat: coord.lat, lng: coord.lng };

    if (currentLevel === "state") {
      setSelection({ state: { id: group.id, name: group.name } });
      setSelectedNodes({ state: node });
    } else if (currentLevel === "district") {
      setSelection((s) => ({ ...s, district: { id: group.id, name: group.name } }));
      setSelectedNodes((n) => ({ ...n, district: node }));
    } else if (currentLevel === "taluka") {
      setSelection((s) => ({ ...s, taluka: { id: group.id, name: group.name } }));
      setSelectedNodes((n) => ({ ...n, taluka: node }));
    } else {
      setSelectedNodes((n) => ({ ...n, village: node }));
    }

    setFocus({ center: [coord.lat, coord.lng], zoom: ZOOM_BY_LEVEL[currentLevel] });
  };

  const goToLevel = (level: "root" | "state" | "district" | "taluka") => {
    if (level === "root") {
      setSelection({});
      setSelectedNodes({});
      setFocus({ center: null, zoom: 6 });
      return;
    }
    if (level === "state") {
      setSelection({ state: selection.state });
      setSelectedNodes({ state: selectedNodes.state });
      if (selectedNodes.state) setFocus({ center: [selectedNodes.state.lat, selectedNodes.state.lng], zoom: ZOOM_BY_LEVEL.state });
      return;
    }
    if (level === "district") {
      setSelection({ state: selection.state, district: selection.district });
      setSelectedNodes({ state: selectedNodes.state, district: selectedNodes.district });
      if (selectedNodes.district) setFocus({ center: [selectedNodes.district.lat, selectedNodes.district.lng], zoom: ZOOM_BY_LEVEL.district });
      return;
    }
    setSelection({ state: selection.state, district: selection.district, taluka: selection.taluka });
    setSelectedNodes({ state: selectedNodes.state, district: selectedNodes.district, taluka: selectedNodes.taluka });
    if (selectedNodes.taluka) setFocus({ center: [selectedNodes.taluka.lat, selectedNodes.taluka.lng], zoom: ZOOM_BY_LEVEL.taluka });
  };

  const handleSearchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const results = filterPartners(partners, query);
    setSearchResults(results);

    const first = results[0];
    if (!first || !first.state_id || !first.state_name) {
      setSelection({});
      setSelectedNodes({});
      setHighlight(null);
      return;
    }

    setActiveTab(SHOW_SUPER_PARTNER && results.some((p) => p.type === "super_partner") ? "super_partner" : "partner");

    // Scope the map only as deep as every matched partner still shares —
    // e.g. searching a district name should show every taluka pin in that
    // district, not zoom into whichever partner happened to match first.
    const sameState = allShareField(results, "state_id");
    const sameDistrict = sameState && Boolean(first.district_id) && allShareField(results, "district_id");
    const sameTaluka = sameDistrict && Boolean(first.taluka_id) && allShareField(results, "taluka_id");
    const sameVillage = sameTaluka && Boolean(first.village_id) && allShareField(results, "village_id");

    const stateCoord = getStateCentroid(first.state_name);
    const newSelection: LocationSelection = { state: { id: first.state_id, name: first.state_name } };
    const newSelectedNodes: SelectedNodes = {};
    if (stateCoord) newSelectedNodes.state = { id: first.state_id, name: first.state_name, ...stateCoord };

    let focusCoord = stateCoord;
    let focusLevel: LocationLevel = "state";
    let label = first.state_name;

    if (sameDistrict && first.district_id && first.district_name) {
      newSelection.district = { id: first.district_id, name: first.district_name };
      const districtCoord = await getLocationCentroid("district", first.district_id, [first.district_name, first.state_name]);
      if (districtCoord) {
        newSelectedNodes.district = { id: first.district_id, name: first.district_name, ...districtCoord };
        focusCoord = districtCoord;
        focusLevel = "district";
        label = first.district_name;
      }

      if (sameTaluka && first.taluka_id && first.taluka_name) {
        newSelection.taluka = { id: first.taluka_id, name: first.taluka_name };
        const talukaCoord = await getLocationCentroid("taluka", first.taluka_id, [
          first.taluka_name,
          first.district_name,
          first.state_name,
        ]);
        if (talukaCoord) {
          newSelectedNodes.taluka = { id: first.taluka_id, name: first.taluka_name, ...talukaCoord };
          focusCoord = talukaCoord;
          focusLevel = "taluka";
          label = first.taluka_name;
        }

        if (sameVillage && first.village_id && first.village_name) {
          const villageCoord = await getLocationCentroid("village", first.village_id, [
            first.village_name,
            first.taluka_name,
            first.district_name,
            first.state_name,
          ]);
          if (villageCoord) {
            newSelectedNodes.village = { id: first.village_id, name: first.village_name, ...villageCoord };
            focusCoord = villageCoord;
            focusLevel = "village";
            label = results.length === 1 ? first.name : first.village_name;
          }
        }
      }
    }

    setSelection(newSelection);
    setSelectedNodes(newSelectedNodes);
    if (focusCoord) {
      setFocus({ center: [focusCoord.lat, focusCoord.lng], zoom: ZOOM_BY_LEVEL[focusLevel] });
      setHighlight({ lat: focusCoord.lat, lng: focusCoord.lng, label });
    } else {
      setHighlight(null);
    }
  };

  const clearSearch = () => {
    setQuery("");
    setSearchResults(null);
    setHighlight(null);
  };

  // The orange "Partners in this area" side panel (names, phone numbers,
  // addresses) is hidden for now; the map takes the full width. Set
  // SHOW_PARTNER_LIST_PANEL to true to bring the panel back.
  const SHOW_PARTNER_LIST_PANEL = false;
  const showList =
    SHOW_PARTNER_LIST_PANEL && (searchResults !== null || Boolean(selection.state));
  const listPartners =
    searchResults ??
    (selectedNodes.village
      ? scopedPartners.filter((p) => String(p.village_id) === String(selectedNodes.village!.id))
      : scopedPartners);

  const stateLabel = selectedNodes.state?.name ?? t("all_india");
  const superPartnersList = listPartners.filter((p) => p.type === "super_partner");
  const partnersList = listPartners.filter((p) => p.type === "partner");
  const superPartnerCount = superPartnersList.length;
  const partnerCount = partnersList.length;

  useEffect(() => {
    setSelectedSuperPartner(null);
  }, [listPartners]);
  const villagesCount = useMemo(() => {
    const ids = new Set(
      listPartners.map((p) => p.village_id).filter((id): id is number => id != null)
    );
    return ids.size;
  }, [listPartners]);

  return (
    <div className="md:flex md:h-screen md:flex-row-reverse md:pt-24">
      <section className="section-padding pt-24 md:pt-0 pb-0 bg-brack h-screen md:h-full md:flex-1">
        <div className="container relative mx-auto h-full px-4">
          <div className="w-full absolute inset-0 z-0">
            <LeafletMap
              markers={markers}
              onMarkerClick={handleMarkerClick}
              focusCenter={focus.center}
              focusZoom={focus.zoom}
              highlight={highlight}
              userLocation={userLocation}
              nearbyMarkerId={currentLevel === "state" && nearbyInfo ? String(nearbyInfo.stateId) : null}
            />
          </div>

          <div className="absolute inset-0 z-10 bg-black/20 pointer-events-none" />

          {locationConsent === "prompt" && (
            <div className="absolute top-4 left-1/2 z-40 flex w-[92%] max-w-md -translate-x-1/2 flex-col items-center gap-2 bg-black/70 px-4 py-3 text-center text-sm text-white backdrop-blur sm:flex-row sm:justify-between sm:text-left">
              <span>{t("location_prompt_text")}</span>
              <div className="flex shrink-0 gap-2">
                <button
                  type="button"
                  onClick={requestLocation}
                  className="bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary/90"
                >
                  {t("allow_location")}
                </button>
                <button
                  type="button"
                  onClick={declineLocation}
                  className="border border-white/40 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/10"
                >
                  {t("not_now")}
                </button>
              </div>
            </div>
          )}

          {locationConsent === "granted" && nearbyInfo && (
            <div className="pointer-events-none absolute top-4 left-1/2 z-40 -translate-x-1/2 bg-blue-600 px-4 py-2 text-center text-sm font-semibold text-white shadow">
              {nearbyInfo.count} {partnersLabel(nearbyInfo.count, t)} {t("near_you")}
            </div>
          )}

          {/* Breadcrumb */}
          <div className="absolute top-4 left-4 z-30 flex items-center gap-1 flex-wrap text-sm bg-black/50 backdrop-blur text-white px-3 py-2 rounded-tl-xl rounded-br-xl">
            <button type="button" onClick={() => goToLevel("root")} className="hover:underline">
              {t("all_india")}
            </button>
            {selectedNodes.state && (
              <>
                <FiChevronRight />
                <button type="button" onClick={() => goToLevel("state")} className="hover:underline">
                  {selectedNodes.state.name}
                </button>
              </>
            )}
            {selectedNodes.district && (
              <>
                <FiChevronRight />
                <button type="button" onClick={() => goToLevel("district")} className="hover:underline">
                  {selectedNodes.district.name}
                </button>
              </>
            )}
            {selectedNodes.taluka && (
              <>
                <FiChevronRight />
                <button type="button" onClick={() => goToLevel("taluka")} className="hover:underline">
                  {selectedNodes.taluka.name}
                </button>
              </>
            )}
            {selectedNodes.village && (
              <>
                <FiChevronRight />
                <span>{selectedNodes.village.name}</span>
              </>
            )}
          </div>

          {/* Legend */}
          <div className="pointer-events-none absolute top-16 left-4 z-30 flex flex-col gap-1.5 rounded-tl-xl rounded-br-xl bg-black/50 backdrop-blur px-3 py-2 text-xs text-white">
            {SHOW_SUPER_PARTNER && (
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: "#7C3AED" }} />
                {t("type_super_partner")}
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: "#F29A30" }} />
              {t("type_partner")}
            </div>
          </div>

          {error && (
            <div className="pointer-events-none absolute top-4 right-4 z-30 bg-red-600/90 text-white text-sm px-3 py-2 rounded-tl-xl rounded-br-xl">
              {error}
            </div>
          )}

          {/* Stats */}
          <MapOverlayStats
            stateLabel={stateLabel}
            villages={formatNumberByLocale(locale, villagesCount)}
            partners={formatNumberByLocale(locale, partnerCount)}
            superPartners={formatNumberByLocale(locale, superPartnerCount)}
            showSuperPartners={SHOW_SUPER_PARTNER}
          />

          {/* Search Form */}
          <form
            onSubmit={handleSearchSubmit}
            className="max-w-xl m-auto mt-14 absolute bottom-6 left-1/2 -translate-x-1/2 w-full px-4 z-[999]"
          >
            <div className="flex items-center">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("placeholder_search")}
                  className="w-full pl-5 pr-10 py-3 text-white bg-black/50 backdrop-blur rounded-bl-2xl focus:outline-none focus:ring-2 focus:ring-white placeholder-white/70"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-200">
                  <FiSearch className="w-5 h-5" />
                </span>
              </div>

              <Button
                type="submit"
                className={`bg-primary text-white px-6 py-3 h-[54px] font-semibold hover:bg-primary/90 transition ${
                  searchResults !== null ? "" : "rounded-tr-2xl"
                }`}
              >
                {t("button_find")}
              </Button>

              {searchResults !== null && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="h-[54px] shrink-0 whitespace-nowrap rounded-tr-2xl bg-black/50 px-4 text-sm font-semibold text-white backdrop-blur hover:bg-black/70 transition"
                >
                  {t("clear_search")}
                </button>
              )}
            </div>
          </form>
        </div>
      </section>

      {showList && (
        <section className="bg-primary section-padding md:h-full md:w-full md:max-w-sm md:shrink-0 md:overflow-y-auto md:border-r md:border-black/10">
          <div className="container mx-auto px-4 md:px-4 md:py-4">
            <div className="flex flex-col items-center gap-2 mb-2 text-center">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-white">
                <FiUsers className="h-5 w-5 text-white" />
                {searchResults ? t("search_results") : t("partners_in_area")}
                <span className="font-normal text-white/80">({listPartners.length})</span>
              </h2>
            </div>

            {listPartners.length === 0 ? (
              <p className="text-white/80">{t("no_results")}</p>
            ) : (
              <Tabs
                value={activeTab}
                onValueChange={(v) => setActiveTab(v as "super_partner" | "partner")}
                className="w-full"
              >
                <TabsList className={`!grid w-full ${SHOW_SUPER_PARTNER ? "grid-cols-2" : "grid-cols-1"} gap-2 !bg-black/20`}>
                  <TabsTrigger
                    value="partner"
                    className="!text-white data-[state=active]:!bg-white data-[state=active]:!text-primary"
                  >
                    {t("type_partner")} ({partnerCount})
                  </TabsTrigger>
                  {SHOW_SUPER_PARTNER && (
                    <TabsTrigger
                      value="super_partner"
                      className="!text-white data-[state=active]:!bg-white data-[state=active]:!text-primary"
                    >
                      {t("type_super_partner")} ({superPartnerCount})
                    </TabsTrigger>
                  )}
                </TabsList>

                <TabsContent value="super_partner">
                  {selectedSuperPartner ? (
                    <div>
                      <button
                        type="button"
                        onClick={() => setSelectedSuperPartner(null)}
                        className="mb-3 flex items-center gap-1 text-sm font-medium text-white underline"
                      >
                        <FiChevronLeft className="h-4 w-4" />
                        {t("back_to_super_partners")}
                      </button>
                      <div className="mb-2 text-sm text-white/80">
                        {t("partners_under")} <span className="font-semibold text-white">{selectedSuperPartner.name}</span>
                      </div>
                      {selectedSuperPartner.sub_partners?.length ? (
                        <div className="space-y-3">
                          {selectedSuperPartner.sub_partners.map((sp) => (
                            <div key={sp.id} className="rounded-lg bg-white/70 p-3">
                              <div className="font-semibold text-dark">{sp.name}</div>
                              <div className="mt-1 flex items-center gap-1 text-sm text-gray-600">
                                <FiMapPin className="h-3.5 w-3.5 shrink-0" />
                                <span className="truncate">{sp.village_name || t("unassigned_location")}</span>
                              </div>
                              <ContactInfo p={sp} />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="py-6 text-white/80">{t("no_results")}</p>
                      )}
                    </div>
                  ) : superPartnersList.length === 0 ? (
                    <p className="py-6 text-white/80">{t("no_results")}</p>
                  ) : (
                    <div className="space-y-3">
                      {superPartnersList.map((p) => {
                        const hasSubPartners = (p.sub_partners?.length ?? 0) > 0;
                        const Wrapper = hasSubPartners ? "button" : "div";
                        return (
                          <Wrapper
                            key={p.id}
                            type={hasSubPartners ? "button" : undefined}
                            onClick={hasSubPartners ? () => setSelectedSuperPartner(p) : undefined}
                            className={`w-full rounded-lg bg-white/70 p-3 text-left ${
                              hasSubPartners ? "hover:bg-white/90" : ""
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="font-semibold text-dark">{p.name}</div>
                              {hasSubPartners && (
                                <div className="flex shrink-0 items-center gap-1 text-xs text-gray-600">
                                  {p.sub_partners.length} {partnersLabel(p.sub_partners.length, t)}
                                  <FiChevronRight className="h-3.5 w-3.5" />
                                </div>
                              )}
                            </div>
                            <div className="mt-1 flex items-center gap-1 text-sm text-gray-600">
                              <FiMapPin className="h-3.5 w-3.5 shrink-0" />
                              <span className="truncate">{p.taluka_name || t("unassigned_location")}</span>
                            </div>
                            <ContactInfo p={p} />
                          </Wrapper>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="partner">
                  {partnersList.length === 0 ? (
                    <p className="py-6 text-white/80">{t("no_results")}</p>
                  ) : (
                    <div className="space-y-3">
                      {partnersList.map((p) => (
                        <div key={p.id} className="rounded-lg bg-white/70 p-3">
                          <div className="font-semibold text-dark">{p.name}</div>
                          <div className="mt-1 flex items-center gap-1 text-sm text-gray-600">
                            <FiMapPin className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{p.village_name || t("unassigned_location")}</span>
                          </div>
                          <ContactInfo p={p} />
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default Page;
